/**
 * Served tenant branding (retest item 4 — "every tenant still renders KycHub
 * branding" because src/theme/tenants.ts is a local fallback registry).
 *
 * The be now serves a per-tenant branding object ({name, brandColor, …,
 * support}) on the session-entry/bootstrap reads. This module is the single
 * seam between that UNTRUSTED wire block and the flow's `TenantConfig`:
 *
 *   • `parseServedBranding` sanitises the raw wire value — strings trimmed,
 *     colours must be real #hex, logo/support URLs must be https (a served
 *     `javascript:` URL must never reach an <img src> or href), and a block
 *     with nothing usable in it parses to null (an older be that serves no
 *     branding is indistinguishable from no branding — the honest absence).
 *   • `resolveTenant` PINS the precedence: served branding WINS over the
 *     local fallback registry, field by field; the registry (and its safe
 *     `kychub` default) is the fallback ONLY where the server said nothing.
 *
 * The regime never comes from this wire — it stays on the session read — and
 * no component ever reads the raw wire block directly.
 */

import type { TenantConfig, TenantSupport } from '@/types';
import { getTenant } from './tenants';

export interface ServedTenantBranding {
  readonly name?: string;
  readonly cobrand?: string;
  readonly brandColor?: string;
  readonly brandColorStrong?: string;
  readonly brandContrast?: string;
  readonly logoUrl?: string;
  readonly initial?: string;
  readonly support?: TenantSupport;
}

const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
}

function asHexColour(v: unknown): string | undefined {
  const s = asString(v);
  return s !== undefined && HEX_COLOUR.test(s) ? s : undefined;
}

/** https-only — a served URL lands in <img src>/<a href>; anything else is dropped. */
function asHttpsUrl(v: unknown): string | undefined {
  const s = asString(v);
  if (s === undefined) return undefined;
  try {
    const url = new URL(s);
    return url.protocol === 'https:' ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

function parseSupport(raw: unknown): TenantSupport | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const r = raw as Record<string, unknown>;
  const phone = asString(r.phone);
  const email = asString(r.email);
  const url = asHttpsUrl(r.url);
  const hours = asString(r.hours);
  if (phone === undefined && email === undefined && url === undefined && hours === undefined) {
    return undefined;
  }
  return {
    ...(phone !== undefined ? { phone } : {}),
    ...(email !== undefined ? { email } : {}),
    ...(url !== undefined ? { url } : {}),
    ...(hours !== undefined ? { hours } : {}),
  };
}

/**
 * Sanitise the raw wire branding block. Returns null when the block is absent,
 * malformed, or carries nothing usable — the caller keeps current behaviour
 * (the fallback registry) in exactly that case, never on a partial block.
 */
export function parseServedBranding(raw: unknown): ServedTenantBranding | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const name = asString(r.name);
  const cobrand = asString(r.cobrand);
  const brandColor = asHexColour(r.brandColor);
  const brandColorStrong = asHexColour(r.brandColorStrong);
  const brandContrast = asHexColour(r.brandContrast);
  const logoUrl = asHttpsUrl(r.logoUrl);
  const initial = asString(r.initial)?.slice(0, 2);
  const support = parseSupport(r.support);

  if (
    name === undefined &&
    cobrand === undefined &&
    brandColor === undefined &&
    logoUrl === undefined &&
    support === undefined
  ) {
    return null; // nothing usable — honest absence, not a half-branded tenant
  }
  return {
    ...(name !== undefined ? { name } : {}),
    ...(cobrand !== undefined ? { cobrand } : {}),
    ...(brandColor !== undefined ? { brandColor } : {}),
    ...(brandColorStrong !== undefined ? { brandColorStrong } : {}),
    ...(brandContrast !== undefined ? { brandContrast } : {}),
    ...(logoUrl !== undefined ? { logoUrl } : {}),
    ...(initial !== undefined ? { initial } : {}),
    ...(support !== undefined ? { support } : {}),
  };
}

/**
 * The pinned precedence: SERVED branding wins over the hardcoded registry;
 * the registry is the fallback only where the server said nothing (older be,
 * or a tenant with no branding configured). Field-by-field, so a tenant that
 * serves only a name still gets it without losing the safe default colours.
 */
export function resolveTenant(
  tenantId: string | null | undefined,
  served: ServedTenantBranding | null,
  /** The SESSION's regime (GET /sessions/{id}) — regime is session truth, never the theme registry's. */
  regime?: TenantConfig['regime'] | null,
): TenantConfig {
  const base0 = getTenant(tenantId);
  const base = regime ? { ...base0, regime } : base0;
  if (!served) return base;
  const branding =
    served.brandColor !== undefined
      ? {
          brandColor: served.brandColor,
          ...(served.brandColorStrong !== undefined
            ? { brandColorStrong: served.brandColorStrong }
            : {}),
          ...(served.brandContrast !== undefined
            ? { brandContrast: served.brandContrast }
            : {}),
          logoUrl: served.logoUrl ?? null,
          ...(served.initial !== undefined
            ? { initial: served.initial }
            : served.name !== undefined
              ? { initial: served.name.charAt(0) }
              : {}),
        }
      : served.logoUrl !== undefined
        ? { ...base.branding, logoUrl: served.logoUrl }
        : base.branding;
  const support = served.support ?? base.support;
  const cobrand = served.cobrand ?? base.cobrand;
  return {
    id: base.id,
    regime: base.regime,
    name: served.name ?? base.name,
    branding,
    ...(cobrand !== undefined ? { cobrand } : {}),
    ...(support !== undefined ? { support } : {}),
  };
}
